import { Flex, Text } from "@mantine/core";
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import BiofoulingSections from "src/components/pages/Settings/YachtSetUp/Biofouling/BiofoulingSections";
import YachtDetailsForm from "src/components/pages/Settings/YachtSetUp/YachtDetailsForm";
import YachtSetupSectionHeader from "src/components/pages/Settings/YachtSetUp/YachtSetupSectionHeader";
import Select from "src/components/shared/_FormInputs/Select";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi, usePostApi } from "src/hooks";
import { PageContainer } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import { type IBiofoulingSetupForm } from "./BiofoulingSetUp.types";
import { mapBiofoulingSetupFields } from "./BiofoulingSetUp.utils";

const BiofoulingSetUp = () => {
  const { data, isLoading: isGetLoading } = useGetApi({
    request: "get-settings",
    body: { params: { sect: "_biofouling" } },
    mapper: mapBiofoulingSetupFields,
  });

  const { post, isLoading: isPostLoading } = usePostApi();

  const methods = useForm<IBiofoulingSetupForm>({
    values: data,
  });

  const { append, ...fieldArrayProps } = useFieldArray<IBiofoulingSetupForm>({
    control: methods.control,
    name: "areas",
  });

  const handleAppend = () => {
    const section = methods.getValues("section");
    append({ name: "", desc: "", section });
  };

  const onSubmit: SubmitHandler<IBiofoulingSetupForm> = async data => {
    const { section, areas, ...rest } = data;

    const sections: any = {};

    if (areas) {
      areas.forEach(item => {
        if (sections[item.section]) {
          sections[item.section].push({ name: item.name, desc: item.desc });
        } else {
          sections[item.section] = [{ name: item.name, desc: item.desc }];
        }
      });
    }

    const list = Object.entries(sections).map(([key, value]) => ({
      section: key,
      list: value,
    }));

    await post({
      request: "save-yacht-info",
      body: {
        params: {
          data: {
            ...rest,
            areas: list,
          },
          section: "biofoul",
          yachtid: getYachtId(),
        },
      },
      onSuccess: () => {
        methods.reset(data);
        Notification(
          "Success",
          "Biofouling Set Up has been successfully changed.",
          "green",
        );
      },
    });
  };

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <YachtSetupSectionHeader
            isSaving={isPostLoading}
            onAdd={handleAppend}
            section="biofoul">
            <Flex gap={15} align="center">
              <Text>Section</Text>
              <Select
                miw={300}
                control={methods.control}
                name="section"
                data={[
                  "External Hull Surfaces",
                  "Hull appendages and fitting",
                  "Steering, propulsion and positioning",
                  "Intake and internal seawater systems",
                ]}
              />
            </Flex>
          </YachtSetupSectionHeader>
          {!data || isGetLoading ? (
            <Loader />
          ) : (
            <Flex wrap="wrap" justify="space-around">
              <YachtDetailsForm type="biofoul" />
              <BiofoulingSections {...fieldArrayProps} />
            </Flex>
          )}
        </form>
      </FormProvider>
    </PageContainer>
  );
};

export default BiofoulingSetUp;
